/**
 * CreateDropModal Component - Form for creating a new drop for a trip
 */

import React, { useState, useEffect } from "react";
import {
  Modal,
  Form,
  Input,
  InputNumber,
  Select,
  Button,
  Space,
  Typography,
  Alert,
} from "antd";
import { PlusOutlined } from "@ant-design/icons";
import { getOrders } from "../../orders/services/orderService";
import { createDrop, getDropsByTrip } from "../services/dropService";
import { logError, logInfo } from "../../../shared/utils/logger.js";

const { TextArea } = Input;
const { Text } = Typography;
const { Option } = Select;

const CreateDropModal = ({ open, tripId, onSuccess, onCancel }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [orders, setOrders] = useState([]);
  const [loadingOrders, setLoadingOrders] = useState(false);
  const [error, setError] = useState(null);
  const [selectedOrderIds, setSelectedOrderIds] = useState([]);

  useEffect(() => {
    if (open && tripId) {
      fetchOrdersAndPreSelect();
      form.resetFields();
      setError(null);
      setSelectedOrderIds([]); // Reset selected orders when modal opens
    }
  }, [open, tripId]);

  const fetchOrdersAndPreSelect = async () => {
    try {
      setLoadingOrders(true);
      
      // Fetch all orders for this trip
      const ordersResponse = await getOrders({ tripId });
      const allTripOrders = ordersResponse.success && ordersResponse.data 
        ? ordersResponse.data 
        : [];

      // Fetch existing drops for this trip
      let existingDrops = [];
      try {
        existingDrops = await getDropsByTrip(tripId);
      } catch (dropError) {
        logError("Failed to fetch existing drops", { 
          tripId,
          error: dropError.message 
        });
        // Continue with empty drops array if fetch fails
      }

      // Extract order IDs that are already assigned to drops
      const assignedOrderIds = new Set();
      existingDrops.forEach(drop => {
        if (drop.orders && drop.orders.length > 0) {
          drop.orders.forEach(order => {
            const orderId = typeof order === 'string' ? order : order.orderId;
            if (orderId) {
              assignedOrderIds.add(orderId);
            }
          });
        }
      });

      // Filter to get unassigned orders
      const unassignedOrders = allTripOrders.filter(
        order => !assignedOrderIds.has(order.id)
      );

      // Pre-select unassigned order IDs
      const unassignedOrderIds = unassignedOrders.map(order => order.id);

      setOrders(allTripOrders); // Show all orders in dropdown
      setSelectedOrderIds(unassignedOrderIds);
      
      // Set form initial values with pre-selected orders
      form.setFieldsValue({
        city: "",
        province: "",
        address: "",
        sequence: 1,
        orderIds: unassignedOrderIds,
      });

      logInfo("CreateDropModal: Pre-selected unassigned orders", {
        tripId,
        totalOrders: allTripOrders.length,
        assignedOrders: assignedOrderIds.size,
        unassignedOrders: unassignedOrderIds.length,
        unassignedOrderIds,
      });
    } catch (err) {
      logError("Failed to fetch orders and pre-select", { 
        tripId,
        error: err.message 
      });
      // Still fetch orders even if drop fetch fails
      try {
        const response = await getOrders({ tripId });
        if (response.success && response.data) {
          setOrders(response.data || []);
        }
      } catch (orderErr) {
        logError("Failed to fetch orders", { error: orderErr.message });
      }
    } finally {
      setLoadingOrders(false);
    }
  };

  const handleSubmit = async (values) => {
    try {
      setLoading(true);
      setError(null);

      // Get current form values as fallback
      const formValues = form.getFieldsValue();
      
      // Use formValues.orderIds as fallback if values.orderIds is empty
      // Also check selectedOrderIds state as final fallback
      const orderIds = values.orderIds?.length > 0 
        ? values.orderIds 
        : (formValues.orderIds?.length > 0 
          ? formValues.orderIds 
          : (selectedOrderIds?.length > 0 ? selectedOrderIds : []));

      const dropData = {
        location: {
          city: values.city,
          province: values.province,
          address: values.address || "",
          coordinates: values.coordinates
            ? {
                lat: values.coordinates.lat,
                lng: values.coordinates.lng,
              }
            : undefined,
        },
        sequence: values.sequence,
        orderIds: orderIds, // Use the determined orderIds
      };

      const result = await createDrop(tripId, dropData);
      if (result.success) {
        logInfo("Drop created successfully", { dropId: result.data?.id });
        form.resetFields();
        setSelectedOrderIds([]); // Clear selected orders after successful submission
        if (onSuccess) {
          onSuccess(result.data);
        }
      } else {
        setError(result.error || "Failed to create drop");
      }
    } catch (err) {
      logError("Failed to create drop", { error: err.message });
      setError(err.message || "Failed to create drop");
    } finally {
      setLoading(false);
    }
  };

  const PROVINCES = [
    "Western Cape",
    "Eastern Cape",
    "KwaZulu-Natal",
    "Gauteng",
    "Limpopo",
    "Mpumalanga",
    "North West",
    "Northern Cape",
    "Free State",
  ];

  return (
    <Modal
      title={
        <Space>
          <PlusOutlined style={{ color: "#1890ff" }} />
          <Text strong>Create New Drop</Text>
        </Space>
      }
      open={open}
      onCancel={onCancel}
      footer={null}
      width={700}
      destroyOnHidden
    >
      {error && (
        <Alert
          message="Error"
          description={error}
          type="error"
          showIcon
          style={{ marginBottom: 24 }}
          closable
          onClose={() => setError(null)}
        />
      )}

      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        initialValues={{
          city: "",
          province: "",
          address: "",
          sequence: 1,
          orderIds: [],
        }}
      >
        <Form.Item
          name="city"
          label="City"
          rules={[
            {
              required: true,
              message: "Please enter the city",
            },
          ]}
        >
          <Input placeholder="Enter city name" />
        </Form.Item>

        <Form.Item
          name="province"
          label="Province"
          rules={[
            {
              required: true,
              message: "Please select a province",
            },
          ]}
        >
          <Select placeholder="Select province" showSearch>
            {PROVINCES.map((province) => (
              <Option key={province} value={province}>
                {province}
              </Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item name="address" label="Address (Optional)">
          <TextArea
            rows={2}
            placeholder="Enter full address..."
          />
        </Form.Item>

        <Form.Item
          name="sequence"
          label="Sequence Number"
          rules={[
            {
              required: true,
              message: "Please enter sequence number",
            },
            {
              type: "number",
              min: 1,
              message: "Sequence must be at least 1",
            },
          ]}
        >
          <InputNumber
            min={1}
            style={{ width: "100%" }}
            placeholder="Enter sequence (1, 2, 3...)"
          />
        </Form.Item>

        <Form.Item 
          name="orderIds" 
          label="Orders (Optional)"
          getValueFromEvent={(value) => {
            // Ensure we always return an array
            if (!value) return [];
            const result = Array.isArray(value) ? value : [value];
            setSelectedOrderIds(result); // Track for debugging
            return result;
          }}
        >
          <Select
            mode="multiple"
            placeholder="Select orders for this drop"
            loading={loadingOrders}
            optionFilterProp="label"
            showSearch
            onChange={(value) => {
              setSelectedOrderIds(value || []);
              form.setFieldsValue({ orderIds: value || [] });
            }}
          >
            {orders.map((order) => {
              const label = `${order.id} - ${order.delivery?.address || "N/A"}`;
              return (
                <Option key={order.id} value={order.id} label={label}>
                  {order.id} - {order.delivery?.address || "N/A"}
                </Option>
              );
            })}
          </Select>
        </Form.Item>
        <Form.Item label="" colon={false}>
          <Text type="secondary" style={{ fontSize: "12px", display: "block", marginTop: -16 }}>
            {selectedOrderIds.length > 0
              ? `${selectedOrderIds.length} order${selectedOrderIds.length > 1 ? 's' : ''} selected`
              : "Select orders to be delivered at this drop location"}
          </Text>
        </Form.Item>

        <Form.Item>
          <Space style={{ width: "100%", justifyContent: "flex-end" }}>
            <Button onClick={onCancel}>Cancel</Button>
            <Button type="primary" htmlType="submit" loading={loading}>
              Create Drop
            </Button>
          </Space>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default CreateDropModal;

